import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {SelectChangeEvent} from '@mui/material/Select/SelectInput';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';

import {STORAGE_KYC_ADDRESSES_FORM_FIELDS} from '@/app/auth/constants/KycConstants';
import {IConfirmAddress} from '@/app/shared-components/ConfirmAddressModal/ConfirmAddressContent';
import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {
  getAddressFromInputValue,
  IAddressFormErrors,
  isPoBoxAddress,
  ParsedAddress,
  parseGooglePlaceToAddress,
  validateAddressForm,
} from '@/app/utils/AddressUtils/AddressUtils';
import {US_STATES} from '@/app/utils/AddressUtils/usStates';
import {useLoading} from '@/app/zustand/useLoading';
import {featureFlags} from '@/config/featureFlags';
import {LINKS} from '@/constants/LinksConstants';
import {AddressFormKyc} from '@/dataSource/src/api/kyc/useRepository';
import useWindowSize from '@/hooks/WindowSize/WindowSize';
import {WEB_VIEW_MESSAGES} from '@/services/webview/constants';
import {WebViewService} from '@/services/webview/WebViewService';
import logError from '@/utils/errors/logError';

import {useTrackers} from './trackers';

type ViewModelContextType = {
  addressFormErrors: IAddressFormErrors;
  confirmAddress: IConfirmAddress;
  formAddress: AddressFormKyc;
  isGooglePlacesReady: boolean;
  hasNoDetails: boolean;
  autoCompleteRef: any;
  disableButton: boolean;
  googleCity: string;
  showTooltip: boolean;
  setShowTooltip: React.Dispatch<React.SetStateAction<boolean>>;
  showModal: boolean;
  hasErrorAddressLine2?: boolean;
  isMobile: boolean;
  handlePlaceChanged: () => void;
  handleAddressChange: (address: any) => void;
  setFormAddress: (address: Partial<AddressFormKyc>) => void;
  handleNavigateToPlaid: () => void;
  handleSetShowModal: (value: boolean) => void;
  handleOnChangeCheckbox: (value: boolean) => void;
  handleSetValue: (value: string) => void;
  handleChangeAddressLine2?: (value: string) => void;
  handleOpenModakUSAPatriot: () => void;
  handleOnChangeState: (event: SelectChangeEvent<string>) => void;
  handleOnClickContinue: () => void;
  handleOnClickEdit: () => void;
};

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

// Mock for Google Places service
const loadGooglePlaces = (): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, 1000));

const EMPTY_ADDRESS_FORM: AddressFormKyc = {
  addressLineShortened: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  region: '',
};

export function ViewModelProvider({children}: {children: ReactNode}) {
  const isEnabledValidatePoBoxAddress =
    featureFlags.isEnabledValidatePoBoxAddress;

  // Hooks
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {isMobile} = useWindowSize();
  const autoCompleteRef = useRef<google.maps.places.Autocomplete>(null);
  const {
    trackSelectOnboardingHomeAddressContinue,
    trackSelectOnboardingHomeConfirmAddressModalEdit,
    trackSelectOnboardingHomeConfirmAddressModalContinue,
    trackSelectOnboardingSearchHomeAddress,
    trackViewOnboardingHomeAddress,
    trackViewOnboardingHomeAddressPoBox,
  } = useTrackers();
  // States
  const [formAddress, setFormAddressState] =
    useState<AddressFormKyc>(EMPTY_ADDRESS_FORM);

  const [confirmAddress, setConfirmAddress] = useState<IConfirmAddress>({
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
  });

  const [hasErrorAddressLine2, setHasErrorAddressLine2] =
    useState<boolean>(false);
  const [googleCity, setGoogleCity] = useState<string>('');
  const [googleAddressLine2, setGoogleAddressLine2] = useState<string>('');
  const [googleRegion, setGoogleRegion] = useState<string>('');
  const [googleZipCode, setGoogleZipCode] = useState<string>('');
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  const handleSetShowModal = useCallback((value: boolean) => {
    setShowModal(value);
  }, []);

  const [isGooglePlacesReady, setIsGooglePlacesReady] =
    useState<boolean>(false);
  const [hasNoDetails, setHasNoDetails] = useState<boolean>(false);

  const {startLoading, stopLoading} = useLoading();
  const poBoxCacheRef = useRef<Map<string, boolean>>(new Map());

  const setFormAddress = useCallback((updates: Partial<AddressFormKyc>) => {
    setFormAddressState(prev => ({...prev, ...updates}));
  }, []);

  const [lastSelectedAddress, setLastSelectedAddress] =
    useState<ParsedAddress>(null);

  const handleAddressChange = useCallback(
    (address: ParsedAddress) => {
      setLastSelectedAddress(address);
      setGoogleCity(address.city);
      setGoogleAddressLine2(address.addressLine2);
      setGoogleRegion(address.region);
      setGoogleZipCode(address.zipCode);
      setFormAddress({
        addressLine1: address.addressLine1 || '',
        addressLineShortened: address.addressLineShortened || '',
        city: address.city || '',
        zipCode: address.zipCode || '',
        state: address.state || '',
        region: address.region || '',
        ...(address.addressLine2 ? {addressLine2: address.addressLine2} : {}),
      });
    },
    [setFormAddress],
  );

  const handleSetValue = useCallback(
    (value: string) => {
      setFormAddress({addressLine1: value, addressLineShortened: value});
      if (!lastSelectedAddress) return;

      const address = getAddressFromInputValue(value, lastSelectedAddress);

      setFormAddress({
        addressLineShortened: address.addressLineShortened,
        city: address.city,
        state: address.state,
        region: address.region,
        zipCode: address.zipCode,
      });
    },
    [lastSelectedAddress, setFormAddress],
  );

  const handlePlaceChanged = useCallback(() => {
    trackSelectOnboardingSearchHomeAddress();
    if (autoCompleteRef.current) {
      const place = autoCompleteRef.current.getPlace();
      const addressData = parseGooglePlaceToAddress(place);
      handleAddressChange(addressData);
    }
  }, [handleAddressChange, trackSelectOnboardingSearchHomeAddress]);

  useEffect(() => {
    trackViewOnboardingHomeAddress();
    // Comment added since it created a loop and triggered event on any change on the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getFieldChanges = useCallback((): string[] => {
    const fieldChanges = [];
    if (googleCity !== formAddress.city) {
      fieldChanges.push('city');
    }
    if (googleAddressLine2 !== formAddress.addressLine2) {
      fieldChanges.push('addressLine2');
    }
    if (googleRegion !== formAddress.region) {
      fieldChanges.push('region');
    }
    if (googleZipCode !== formAddress.zipCode) {
      fieldChanges.push('zipCode');
    }
    return fieldChanges;
  }, [
    formAddress,
    googleAddressLine2,
    googleCity,
    googleRegion,
    googleZipCode,
  ]);

  const poBoxKey = useMemo(
    () =>
      JSON.stringify({
        line1: formAddress.addressLineShortened,
        city: formAddress.city,
        state: formAddress.region,
        zipCode: formAddress.zipCode,
      }),
    [
      formAddress.addressLineShortened,
      formAddress.city,
      formAddress.region,
      formAddress.zipCode,
    ],
  );

  const isPoBox = useCallback(async () => {
    const cache = poBoxCacheRef.current;

    if (cache.has(poBoxKey)) {
      return cache.get(poBoxKey);
    }

    startLoading();
    let poBoxResult = false;

    try {
      poBoxResult = await isPoBoxAddress({
        line1: formAddress.addressLineShortened,
        city: formAddress.city,
        state: formAddress.region,
        postal_code: formAddress.zipCode,
        country: 'US',
      });

      cache.set(poBoxKey, poBoxResult);
    } finally {
      stopLoading();
    }

    return poBoxResult;
  }, [formAddress, poBoxKey, startLoading, stopLoading]);

  const handleOnClickContinue = useCallback(async () => {
    trackSelectOnboardingHomeAddressContinue(getFieldChanges());
    if (!hasNoDetails && !formAddress.addressLine2) {
      setHasErrorAddressLine2(true);
      showSnackbar({
        summary: t('kyc.not_started.missing_line2.summary'),
        message: t('kyc.not_started.missing_line2.message'),
      });
      return;
    }
    if (isEnabledValidatePoBoxAddress && (await isPoBox())) {
      trackViewOnboardingHomeAddressPoBox(getFieldChanges());
      showSnackbar({
        summary: t('kyc.not_started.po_box_address.summary'),
        message: t('kyc.not_started.po_box_address.message'),
      });
      return;
    }
    setConfirmAddress({
      addressLine1: formAddress.addressLineShortened,
      addressLine2: formAddress.addressLine2,
      city: formAddress.city,
      state: formAddress.region,
      zipCode: formAddress.zipCode,
      country: t('request_card.us'),
    });
    handleSetShowModal(true);
  }, [
    formAddress,
    getFieldChanges,
    handleSetShowModal,
    hasNoDetails,
    isEnabledValidatePoBoxAddress,
    isPoBox,
    t,
    trackSelectOnboardingHomeAddressContinue,
    trackViewOnboardingHomeAddressPoBox,
  ]);

  const handleNavigateToPlaid = useCallback(() => {
    trackSelectOnboardingHomeConfirmAddressModalContinue(getFieldChanges());
    localStorage.setItem(
      STORAGE_KYC_ADDRESSES_FORM_FIELDS,
      JSON.stringify(formAddress),
    );
    navigate('/onboarding/kyc/plaid', {
      state: {
        formAddress,
      },
    });
  }, [
    formAddress,
    getFieldChanges,
    navigate,
    trackSelectOnboardingHomeConfirmAddressModalContinue,
  ]);

  const handleOnClickEdit = useCallback(() => {
    trackSelectOnboardingHomeConfirmAddressModalEdit();
    setShowModal(false);
  }, [trackSelectOnboardingHomeConfirmAddressModalEdit]);

  const handleOnChangeCheckbox = useCallback((value: boolean) => {
    setShowTooltip(false);
    setHasNoDetails(value);
    setHasErrorAddressLine2(false);
  }, []);

  const handleChangeAddressLine2 = useCallback(
    (value: string) => {
      setHasErrorAddressLine2(false);
      setFormAddress({addressLine2: value});
    },
    [setFormAddress],
  );

  const disableButton = useMemo(() => {
    const {addressLineShortened, addressLine1, city, state, zipCode} =
      formAddress;

    // Validate form fields
    const isFormInvalid =
      !addressLineShortened || !addressLine1 || !city || !state || !zipCode;

    return isFormInvalid;
  }, [formAddress]);

  const addressFormErrors: IAddressFormErrors = useMemo(
    () => validateAddressForm(formAddress, !!lastSelectedAddress),
    [formAddress, lastSelectedAddress],
  );

  // Handle Google Places loading
  useEffect(() => {
    const loadPlaces = async () => {
      try {
        await loadGooglePlaces();
        setIsGooglePlacesReady(true);
      } catch (error) {
        logError(error, {
          customTitle: 'KycNotStarted - Error loading Google Places',
          fileName: 'KycNotStarted ViewModelContext',
          functionName: 'useEffect loadGooglePlaces',
        });
      }
    };

    loadPlaces();
  }, []);

  const handleOpenModakUSAPatriot = useCallback(() => {
    if (
      WebViewService.isWebViewActive() &&
      typeof window.ReactNativeWebView !== 'undefined' &&
      window.ReactNativeWebView
    ) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({status: WEB_VIEW_MESSAGES.OPEN_USA_PATRIOT}),
      );
      return;
    }
    window.open(LINKS.MODAK_MAKER_USA_PATRIOT_URL, '_blank');
  }, []);

  const handleOnChangeState = useCallback(
    (event: SelectChangeEvent<string>) => {
      const selectedRegion = event.target.value;

      const selectedState = US_STATES.find(
        state => state.value === selectedRegion,
      );

      setFormAddress({
        region: selectedRegion,
        state: selectedState?.label ?? '',
      });
    },
    [setFormAddress],
  );

  return (
    <ViewModelContext.Provider
      value={{
        addressFormErrors,
        formAddress,
        confirmAddress,
        setFormAddress,
        isGooglePlacesReady,
        handleAddressChange,
        hasNoDetails,
        handlePlaceChanged,
        autoCompleteRef,
        disableButton,
        googleCity,
        showTooltip,
        setShowTooltip,
        showModal,
        isMobile,
        handleNavigateToPlaid,
        handleSetShowModal,
        handleOnChangeCheckbox,
        handleSetValue,
        hasErrorAddressLine2,
        handleChangeAddressLine2,
        handleOpenModakUSAPatriot,
        handleOnChangeState,
        handleOnClickContinue,
        handleOnClickEdit,
      }}>
      {children}
    </ViewModelContext.Provider>
  );
}

export function useViewModelProvider() {
  const context = useContext(ViewModelContext);
  if (!context) {
    throw new Error(
      'KycNotStarted useViewModelProvider must be used within a ViewModelProvider',
    );
  }
  return context;
}
